// src/components/InvoiceDeleteHistory.js
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import moment from "moment";
import {
  Typography,
  Table,
  useTheme,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  Paper,
  Container,
  Button,
  CircularProgress,
  createTheme,
  ThemeProvider,
  IconButton,
  makeStyles,
} from "@material-ui/core";
import { getDeletedInvoices } from "../../actions/invoiceActions"; 
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import FirstPageIcon from "@material-ui/icons/FirstPage";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import LastPageIcon from "@material-ui/icons/LastPage";
import PropTypes from "prop-types";

const colorTheme = createTheme({
  palette: {
    primary: {
      main: "#ef7f1a",
    },
  },
});

const useStyles1 = makeStyles((theme) => ({
  root: {
    flexShrink: 0,
    marginLeft: theme.spacing(2.5),
  },
}));

function TablePaginationActions(props) {
  const classes = useStyles1();
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.root}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight />
        ) : (
          <KeyboardArrowLeft />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft />
        ) : (
          <KeyboardArrowRight />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
}

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const tableStyle = {
  width: 160,
  fontSize: 14,
  cursor: "pointer",
  borderBottom: "none",
  padding: "8px",
  textAlign: "center",
};
const headerStyle = { borderBottom: "none", textAlign: "center" };

const InvoiceDeleteHistory = () => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const history = useHistory();
  const { deletedInvoices, isLoading } = useSelector((state) => state.invoices);
  const user = JSON.parse(localStorage.getItem("profile"));

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    dispatch(getDeletedInvoices());
  }, [dispatch]);

  if (!user) {
    history.push("/login");
  }

  const toCommas = (value) => {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <ThemeProvider theme={colorTheme}>
      <Container style={{ paddingTop: "20px", paddingBottom: "20px" }}>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.push("/invoices")}
          style={{ marginBottom: "20px" }}
        >
          Back to Invoices
        </Button>
        <Typography variant="h4" gutterBottom>
          Deleted Invoices History
        </Typography>

        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress />
          </div>
        ) : deletedInvoices.length === 0 ? (
            <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px", 
            }}
          >
            <Typography variant="h6" color="textSecondary">
              No deleted invoices found.
            </Typography>
          </div>
        ) : (
          <TableContainer component={Paper} elevation={0}>
            <Table aria-label="deleted invoices table">
              <TableHead>
                <TableRow>
                  <TableCell style={headerStyle}>Invoice Number</TableCell>
                  <TableCell style={headerStyle}>Invoice Date</TableCell>
                  <TableCell style={headerStyle}>Customer Name</TableCell>
                  <TableCell style={headerStyle}>Payment Mode</TableCell>{" "}
                  {/* New Column */}
                  <TableCell style={headerStyle}>Amount</TableCell>
                  <TableCell style={headerStyle}>Deleted At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(rowsPerPage > 0
                  ? deletedInvoices.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                  : deletedInvoices
                ).map((invoice) => (
                  <TableRow key={invoice._id}>
                    <TableCell style={tableStyle}>
                      {invoice.invoiceNumber}
                    </TableCell>
                    <TableCell style={tableStyle}>
                      {moment(invoice.createdAt).format("MMM Do YYYY")}
                    </TableCell>
                    <TableCell style={tableStyle}>
                      {invoice?.client?.name || "Cash"}
                    </TableCell>
                    <TableCell style={tableStyle}>
                      {invoice.paymentMode || "N/A"} {/* New Field */}
                    </TableCell>
                    <TableCell style={tableStyle}>
                      {invoice.currency}{" "}
                      {invoice.total
                        ? toCommas(invoice.total.toFixed(2))
                        : invoice.total}
                    </TableCell>
                    <TableCell style={tableStyle}>
                      {moment(invoice.deletedAt).format("MMM Do YYYY, h:mm a")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    rowsPerPageOptions={[
                      5,
                      10,
                      25,
                      { label: "All", value: -1 },
                    ]}
                    colSpan={6}
                    count={deletedInvoices.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    SelectProps={{
                      inputProps: { "aria-label": "rows per page" },
                      native: true,
                    }}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={TablePaginationActions}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </Container>
    </ThemeProvider>
  );
};

export default InvoiceDeleteHistory;


